import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, KeyRound, QrCode, UploadCloud } from "lucide-react";

const EMPTY = {
  provider: "yokke",
  is_production: false,
  has_static_qr_image: false,
  yokke_client_key: "", yokke_client_secret: "", yokke_private_key_pem: "",
  yokke_partner_id: "", yokke_merchant_id: "", yokke_terminal_id: "", yokke_channel_id: "02",
};

const STATIC_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_STATIC_IMAGE_SIZE = 5 * 1024 * 1024;

export default function QrisSettings() {
  const [outlets, setOutlets] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staticFile, setStaticFile] = useState(null);
  const [storedPreviewUrl, setStoredPreviewUrl] = useState("");
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    const [o, c] = await Promise.all([api.get("/outlets"), api.get("/qris-config")]);
    setOutlets(o.data || []);
    setConfigs(c.data || []);
    if (o.data?.[0] && !outletId) setOutletId(o.data[0].id);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!outletId) return;
    const cfg = configs.find((item) => item.level === "outlet" && item.outlet_id === outletId);
    if (cfg) {
      setForm({ ...EMPTY, ...cfg, yokke_client_secret: "", yokke_private_key_pem: "" });
    } else {
      setForm(EMPTY);
    }
    setStaticFile(null);
  }, [outletId, configs]);

  useEffect(() => {
    if (!staticFile) {
      setLocalPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(staticFile);
    setLocalPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [staticFile]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    const cfg = configs.find((item) => item.level === "outlet" && item.outlet_id === outletId);

    setStoredPreviewUrl("");
    setPreviewLoading(false);
    if (!outletId || !cfg?.has_static_qr_image) return undefined;

    setPreviewLoading(true);
    api.get(`/qris-config/outlet/${outletId}/static-image`, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setStoredPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active && cfg.provider === "static") toast.error("Preview QRIS statis tidak dapat dimuat");
      })
      .finally(() => { if (active) setPreviewLoading(false); });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [outletId, configs]);

  const selectStaticFile = (event) => {
    const selected = event.target.files?.[0] || null;
    event.target.value = "";
    if (!selected) return;
    if (!STATIC_IMAGE_TYPES.includes(selected.type)) {
      toast.error("Format QRIS harus JPG, PNG, atau WEBP");
      return;
    }
    if (selected.size > MAX_STATIC_IMAGE_SIZE) {
      toast.error("Ukuran gambar QRIS maksimal 5 MB");
      return;
    }
    setStaticFile(selected);
  };

  const save = async () => {
    if (!outletId) return toast.error("Pilih outlet terlebih dahulu");
    if (form.provider === "static" && !staticFile && !form.has_static_qr_image) {
      return toast.error("Upload gambar QRIS statis terlebih dahulu");
    }

    setSaving(true);
    try {
      if (form.provider === "static" && staticFile) {
        const data = new FormData();
        data.append("file", staticFile);
        await api.post(`/qris-config/outlet/${outletId}/static-image`, data);
      } else {
        await api.put("/qris-config", { level: "outlet", outlet_id: outletId, ...form });
      }
      toast.success("Konfigurasi QRIS outlet tersimpan");
      setStaticFile(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Konfigurasi QRIS gagal disimpan");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await api.post("/qris-config/test-yokke", { level: "outlet", outlet_id: outletId });
      toast.success(`Token Yokke OK (${response.data.token_preview})`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal test");
    } finally {
      setTesting(false);
    }
  };

  const previewUrl = localPreviewUrl || storedPreviewUrl;

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-6">
        <div className="overline text-zinc-500">PEMBAYARAN</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">QRIS Outlet</h1>
        <p className="mt-2 text-sm text-zinc-500 max-w-2xl">Konfigurasi QRIS wajib diatur untuk setiap outlet yang menerima pembayaran QRIS. Konfigurasi platform tidak digunakan untuk transaksi kasir.</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-0 grid-borders">
        <div className="p-6 bg-white">
          <div className="overline text-zinc-500 mb-3">Outlet</div>
          <div className="space-y-1">
            {outlets.map((outlet) => (
              <button key={outlet.id} onClick={() => setOutletId(outlet.id)} data-testid={`qris-outlet-${outlet.id}`}
                className={"w-full text-left px-3 py-2.5 text-sm border-l-2 " + (outletId === outlet.id ? "border-[#002FA7] bg-zinc-50 font-semibold" : "border-transparent hover:bg-zinc-50")}>
                {outlet.name}
              </button>
            ))}
            {outlets.length === 0 && <div className="text-xs text-zinc-500">Belum ada outlet.</div>}
          </div>
        </div>
        <div className="lg:col-span-2 p-6 bg-white">
          <div className="flex items-center gap-2 mb-4"><QrCode className="h-5 w-5"/><div className="overline">Konfigurasi</div></div>
          <div className="space-y-3">
            <div><label className="overline">Jenis QRIS</label>
              <select data-testid="qris-provider" value={form.provider} onChange={(event) => setForm({ ...form, provider: event.target.value })} className="w-full border border-border px-3 py-2 mt-1">
                <option value="yokke">QRIS Dinamis — Yokke / Mandiri</option>
                <option value="static">QRIS Statis</option>
              </select>
            </div>

            {form.provider === "yokke" && (
              <div className="space-y-3 border-l-2 border-[#002FA7] pl-4">
                <div className="overline text-[#002FA7]">YOKKE / MANDIRI SNAP</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="overline">X-CLIENT-KEY (MTI Key)</label>
                    <input data-testid="yokke-client-key" value={form.yokke_client_key} onChange={(event) => setForm({ ...form, yokke_client_key: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-xs"/>
                  </div>
                  <div><label className="overline">Client Secret (HMAC)</label>
                    <input data-testid="yokke-client-secret" type="password" placeholder={form.yokke_client_key ? "(tersimpan — kosongkan untuk tidak ubah)" : ""} value={form.yokke_client_secret} onChange={(event) => setForm({ ...form, yokke_client_secret: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-xs"/>
                  </div>
                </div>
                <div><label className="overline">Private Key (PEM, untuk SHA256withRSA)</label>
                  <textarea data-testid="yokke-private-key" rows={5} placeholder={form.yokke_client_key ? "(tersimpan — kosongkan untuk tidak ubah)" : "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"} value={form.yokke_private_key_pem} onChange={(event) => setForm({ ...form, yokke_private_key_pem: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-[10px] leading-tight"/>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="overline">X-PARTNER-ID</label>
                    <input data-testid="yokke-partner-id" value={form.yokke_partner_id} onChange={(event) => setForm({ ...form, yokke_partner_id: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-xs"/>
                  </div>
                  <div><label className="overline">CHANNEL-ID</label>
                    <input data-testid="yokke-channel-id" value={form.yokke_channel_id} onChange={(event) => setForm({ ...form, yokke_channel_id: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-xs" placeholder="02"/>
                  </div>
                  <div><label className="overline">Production?</label>
                    <label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" checked={form.is_production} onChange={(event) => setForm({ ...form, is_production: event.target.checked })} data-testid="yokke-prod"/>{form.is_production ? "PROD" : "SANDBOX"}</label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="overline">Merchant ID</label>
                    <input data-testid="yokke-merchant-id" value={form.yokke_merchant_id} onChange={(event) => setForm({ ...form, yokke_merchant_id: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-xs"/>
                  </div>
                  <div><label className="overline">Terminal ID</label>
                    <input data-testid="yokke-terminal-id" value={form.yokke_terminal_id} onChange={(event) => setForm({ ...form, yokke_terminal_id: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-xs"/>
                  </div>
                </div>
                <button onClick={testConnection} disabled={testing || !outletId} data-testid="yokke-test-btn" className="w-full text-xs uppercase tracking-widest font-semibold py-2.5 border border-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"><KeyRound className="h-3.5 w-3.5"/>{testing ? "Mengecek…" : "Test Koneksi (getToken)"}</button>
              </div>
            )}

            {form.provider === "static" && (
              <div className="space-y-3 border-l-2 border-emerald-600 pl-4">
                <div className="overline text-emerald-700">QRIS STATIS OUTLET</div>
                <label className="block border-2 border-dashed border-zinc-300 bg-zinc-50 p-5 text-center cursor-pointer hover:border-emerald-600 hover:bg-emerald-50/40">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={selectStaticFile}
                    className="sr-only"
                    data-testid="qris-static-file"
                  />
                  <UploadCloud className="mx-auto h-8 w-8 text-zinc-500"/>
                  <div className="mt-2 text-sm font-semibold">Upload gambar QRIS statis</div>
                  <div className="mt-1 text-xs text-zinc-500">JPG, PNG, atau WEBP · maksimal 5 MB</div>
                  {staticFile && <div className="mt-2 text-xs font-medium text-emerald-700 break-all">File dipilih: {staticFile.name}</div>}
                  {!staticFile && form.has_static_qr_image && <div className="mt-2 text-xs font-medium text-emerald-700">Gambar QRIS sudah tersimpan. Pilih file baru untuk mengganti.</div>}
                </label>

                <div className="border border-border bg-white p-3 min-h-64 flex items-center justify-center">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview QRIS statis" className="w-56 h-56 object-contain"/>
                  ) : previewLoading ? (
                    <div className="text-sm text-zinc-500">Memuat preview QRIS…</div>
                  ) : (
                    <div className="text-center text-zinc-400">
                      <ImageIcon className="mx-auto h-10 w-10"/>
                      <div className="mt-2 text-xs">Belum ada gambar QRIS yang diunggah</div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-amber-700">QRIS statis tidak memiliki inquiry otomatis. Kasir harus mengonfirmasi pembayaran setelah dana terlihat pada rekening merchant.</p>
              </div>
            )}
            <button onClick={save} data-testid="qris-save-btn" disabled={!outletId || saving} className="w-full bg-[#002FA7] text-white py-3 font-semibold disabled:opacity-50">{saving ? "Menyimpan…" : "Simpan"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
